import React, { useState, useEffect } from 'react';
import { UserPlus, ChevronDown, Search, Eye, Check, Download, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function KardexMatriculacionAprobados() {
    const [estudiantes, setEstudiantes] = useState([]);
    const [carreras, setCarreras] = useState([]);
    const [menciones, setMenciones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [modalidad, setModalidad] = useState('CPA');
    const [showModal, setShowModal] = useState(false);
    const [selectedEstudiante, setSelectedEstudiante] = useState(null);
    const [formData, setFormData] = useState({ id_carrera: '', id_mencion: '' });
    const [matriculando, setMatriculando] = useState(false);

    const modalidades = [
        { value: 'CPA', label: 'Curso de Preadmisión (CPA)' },
        { value: 'PAA', label: 'Prueba de Admisión Académica (PAA)' },
        { value: 'Admision Especial', label: 'Admisión Especial' }
    ];

    useEffect(() => {
        loadData();
    }, [modalidad]);

    useEffect(() => {
        loadCarrerasMenciones();
    }, []);

    const loadCarrerasMenciones = async () => {
        try {
            const [carrerasRes, mencionesRes] = await Promise.all([
                fetch(`${API_URL}/jefatura/carreras`),
                fetch(`${API_URL}/jefatura/menciones`)
            ]);
            const carrerasData = await carrerasRes.json();
            const mencionesData = await mencionesRes.json();
            setCarreras(carrerasData.carreras || []);
            setMenciones(mencionesData.menciones || []);
        } catch (error) {
            console.error('Error loading carreras/menciones:', error);
        }
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/kardex/estudiantes-aprobados?modalidad=${modalidad}`);
            const data = await response.json();
            setEstudiantes(data.estudiantes || []);
        } catch (error) {
            console.error('Error loading estudiantes aprobados:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleMatricular = (estudiante) => {
        setSelectedEstudiante(estudiante);
        setFormData({ id_carrera: '', id_mencion: '' });
        setShowModal(true);
    };

    const confirmarMatriculacion = async () => {
        if (!selectedEstudiante) return;

        setMatriculando(true);
        try {
            const response = await fetch(`${API_URL}/kardex/matricular`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id_inscripcion: selectedEstudiante.id_inscripcion,
                    id_carrera: formData.id_carrera || null,
                    id_mencion: formData.id_mencion || null
                })
            });

            if (response.ok) {
                alert(`✅ ${selectedEstudiante.nombres} ${selectedEstudiante.primer_apellido} ha sido matriculado exitosamente`);
                setShowModal(false);
                setSelectedEstudiante(null);
                loadData(); // Recargar lista
            } else {
                const error = await response.json();
                alert(`Error: ${error.message}`);
            }
        } catch (error) {
            console.error('Error matriculando:', error);
            alert('Error al matricular estudiante');
        } finally {
            setMatriculando(false);
        }
    };

    const getMencionesFiltradas = () => {
        if (!formData.id_carrera) return [];
        return menciones.filter(m => m.id_carrera === parseInt(formData.id_carrera));
    };

    const estudiantesFiltrados = estudiantes.filter(est => {
        const nombre = `${est.nombres} ${est.primer_apellido} ${est.segundo_apellido || ''}`.toLowerCase();
        const ci = (est.cedula_identidad || '').toLowerCase();
        const codigo = (est.codigo || '').toLowerCase();
        const term = searchTerm.toLowerCase();
        return nombre.includes(term) || ci.includes(term) || codigo.includes(term);
    });

    // Descargar Certificado de Admisión
    const descargarCertificadoAdmision = (est) => {
        const fechaHoy = new Date().toLocaleDateString('es-BO', { day: '2-digit', month: 'long', year: 'numeric' });
        const gestionActual = new Date().getFullYear();

        const html = `
<!DOCTYPE html>
<html lang="es">
<head>
    <meta charset="UTF-8">
    <title>Certificado de Admisión</title>
    <style>
        body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; margin: 40px; }
        .header { text-align: center; margin-bottom: 20px; }
        .logo { height: 60px; margin-bottom: 10px; }
        .title { font-size: 18px; font-weight: bold; margin: 20px 0; text-transform: uppercase; text-align: center; }
        .content { text-align: justify; line-height: 1.8; margin: 20px 30px; font-size: 13px; }
        .signature { margin-top: 100px; text-align: center; }
        .firma-line { border-top: 1px solid #000; width: 250px; margin: 0 auto; padding-top: 5px; }
        .footer { position: fixed; bottom: 30px; width: 100%; text-align: center; font-size: 10px; color: #666; }
        @page { size: A4; margin: 25mm; }
        @media print { .no-print { display: none; } }
    </style>
</head>
<body>
    <button class="no-print" onclick="window.print()" style="margin-bottom:20px;padding:10px 20px;cursor:pointer;">Imprimir / Guardar PDF</button>
    
    <div class="header">
        <h2>ESCUELA BOLIVIANA INTERCULTURAL DE DANZA</h2>
        <h3>DIRECCIÓN ACADÉMICA</h3>
    </div>
    
    <div class="title">CERTIFICADO DE ADMISIÓN</div>
    
    <div class="content">
        <p>La Dirección Académica de la Escuela Boliviana Intercultural de Danza, certifica que:</p>
        
        <p style="text-align:center;font-weight:bold;font-size:16px;margin:30px 0;">
            ${est.nombres || ''} ${est.primer_apellido || ''} ${est.segundo_apellido || ''}
        </p>
        
        <p>Con Cédula de Identidad N° <strong>${est.cedula_identidad || '________'} ${est.extension || ''}</strong>, 
        ha sido <strong>ADMITIDO(A)</strong> como estudiante regular de esta institución en la Carrera de 
        <strong>${est.carrera_nombre || '________________________'}</strong>${est.mencion_nombre ? `, Mención: ${est.mencion_nombre}` : ''},
        para la gestión académica ${gestionActual}.</p>
        
        <p>Es cuanto se certifica para los fines consiguientes del interesado(a).</p>
    </div>
    
    <p style="text-align:right;margin-top:60px;margin-right:30px;">El Alto, ${fechaHoy}</p>
    
    <div class="signature">
        <div class="firma-line">
            <strong>Director(a) Académico(a)</strong><br>
            EBID
        </div>
    </div>

    <div class="footer">
        Av. Costanera Nro. 100, Zona Villa Tunari - El Alto, Bolivia <br>
        Teléfono: 2-2860000 | Email: info@ebid.edu.bo
    </div>
</body>
</html>`;

        const win = window.open('', '_blank');
        if (win) {
            win.document.write(html);
            win.document.close();
        } else {
            alert('El navegador bloqueó la ventana emergente. Por favor, permite las ventanas emergentes para este sitio.');
        }
    };

    return (
        <div className="p-4 md:p-6">
            <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-800">Kardex &gt; Matriculación de Aprobados</h1>
                <p className="text-sm text-gray-600 mt-1">
                    Estudiantes que aprobaron y están listos para ser matriculados
                </p>
            </div>

            {/* Filtros */}
            <div className="mb-6 flex flex-wrap gap-4 items-center">
                {/* Dropdown Modalidad */}
                <div className="relative">
                    <select
                        value={modalidad}
                        onChange={(e) => setModalidad(e.target.value)}
                        className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-full bg-white text-sm min-w-[280px]"
                    >
                        {modalidades.map(m => (
                            <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                </div>

                {/* Buscador */}
                <div className="relative flex-1 max-w-md">
                    <input
                        type="text"
                        placeholder="Buscar por nombre, CI o código..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm"
                    />
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                </div>
            </div>

            {/* Tabla de Estudiantes Aprobados */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-blue-900 text-white">
                            <tr>
                                <th className="px-4 py-3 text-center font-semibold">No</th>
                                <th className="px-4 py-3 text-center font-semibold">Código</th>
                                <th className="px-4 py-3 text-left font-semibold">Nombre Completo</th>
                                <th className="px-4 py-3 text-center font-semibold">CI</th>
                                <th className="px-4 py-3 text-center font-semibold">Email</th>
                                <th className="px-4 py-3 text-center font-semibold">Promedio</th>
                                <th className="px-4 py-3 text-center font-semibold">Materias</th>
                                <th className="px-4 py-3 text-center font-semibold">Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                                        Cargando...
                                    </td>
                                </tr>
                            ) : estudiantesFiltrados.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                                        {searchTerm
                                            ? 'No se encontraron estudiantes con ese criterio'
                                            : `No hay estudiantes aprobados en ${modalidad} pendientes de matriculación`
                                        }
                                    </td>
                                </tr>
                            ) : (
                                estudiantesFiltrados.map((est, index) => (
                                    <tr key={est.id_inscripcion} className="hover:bg-gray-50 border-b">
                                        <td className="px-4 py-3 text-center">{index + 1}</td>
                                        <td className="px-4 py-3 text-center font-mono text-blue-600">
                                            {est.codigo}
                                        </td>
                                        <td className="px-4 py-3">
                                            {est.nombres} {est.primer_apellido} {est.segundo_apellido || ''}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            {est.cedula_identidad}-{est.extension}
                                        </td>
                                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                                            {est.email || '-'}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <span className={`px-2 py-1 rounded-full font-semibold ${parseFloat(est.promedio_general) >= 70
                                                    ? 'bg-green-100 text-green-700'
                                                    : parseFloat(est.promedio_general) >= 51
                                                        ? 'bg-yellow-100 text-yellow-700'
                                                        : 'bg-red-100 text-red-700'
                                                }`}>
                                                {est.promedio_general || 'N/A'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            {est.materias_con_nota || '-'}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <div className="flex items-center justify-center gap-2">
                                                <button
                                                    onClick={() => handleMatricular(est)}
                                                    className="px-4 py-1.5 bg-green-600 text-white rounded-full hover:bg-green-700 text-sm font-medium flex items-center gap-1"
                                                >
                                                    <UserPlus size={16} />
                                                    Matricular
                                                </button>
                                                <button
                                                    onClick={() => descargarCertificadoAdmision(est)}
                                                    className="p-2 text-orange-600 hover:bg-orange-50 rounded-full transition shadow-sm"
                                                    title="Descargar Certificado de Admisión"
                                                >
                                                    <Download size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer con conteo */}
                <div className="px-4 py-3 bg-gray-50 border-t text-sm text-gray-600">
                    Mostrando {estudiantesFiltrados.length} estudiantes aprobados
                </div>
            </div>

            {/* Modal de Matriculación */}
            {showModal && selectedEstudiante && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="px-6 py-4 border-b">
                            <h2 className="text-xl font-bold text-blue-900">Confirmar Matriculación</h2>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <p className="font-semibold text-lg">
                                    {selectedEstudiante.nombres} {selectedEstudiante.primer_apellido} {selectedEstudiante.segundo_apellido || ''}
                                </p>
                                <p className="text-sm text-gray-600">
                                    CI: {selectedEstudiante.cedula_identidad}-{selectedEstudiante.extension}
                                </p>
                                <p className="text-sm text-gray-600">
                                    Código: {selectedEstudiante.codigo}
                                </p>
                                <p className="text-sm text-gray-600">
                                    Promedio: <strong className="text-green-600">{selectedEstudiante.promedio_general}</strong>
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Carrera</label>
                                <select
                                    value={formData.id_carrera}
                                    onChange={(e) => setFormData({ ...formData, id_carrera: e.target.value, id_mencion: '' })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                    <option value="">Seleccionar carrera...</option>
                                    {carreras.map(c => (
                                        <option key={c.id_carrera} value={c.id_carrera}>{c.nombre}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Mención</label>
                                <select
                                    value={formData.id_mencion}
                                    onChange={(e) => setFormData({ ...formData, id_mencion: e.target.value })}
                                    disabled={!formData.id_carrera}
                                    className={`w-full px-3 py-2 border border-gray-300 rounded-md ${!formData.id_carrera ? 'bg-gray-100' : ''}`}
                                >
                                    <option value="">{formData.id_carrera ? 'Seleccionar mención...' : 'Primero seleccione carrera'}</option>
                                    {getMencionesFiltradas().map(m => (
                                        <option key={m.id_mencion} value={m.id_mencion}>{m.nombre}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="text-sm text-yellow-700 bg-yellow-50 p-3 rounded-lg">
                                <strong>Nota:</strong> Al matricular, los datos del estudiante se copiarán a la tabla de matriculados y no aparecerá más en esta lista.
                            </div>
                        </div>

                        <div className="px-6 py-4 border-t flex justify-end gap-3">
                            <button
                                onClick={() => setShowModal(false)}
                                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmarMatriculacion}
                                disabled={matriculando}
                                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
                            >
                                <Check size={18} />
                                {matriculando ? 'Matriculando...' : 'Confirmar Matriculación'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
